/**
 * Zustand brief-form store — T23 + T6.
 *
 * Holds the complete form state across re-renders.
 * Lifted high enough that OutputPane and RunsSidebar can read productId context.
 *
 * Three-channel field state (T6):
 *   Each parameter field can be in one of three states:
 *   - {kind: 'entry', entryId}     — library entry selected by ID
 *   - {kind: 'value', value}       — writer-typed text (auto-creates library entry on submit)
 *   - {kind: 'ephemeral', ephemeralValue} — AI-suggested text (no library entry created)
 *
 * Backward-compat derived fields (for BriefForm.tsx until T8 migration):
 *   angleEntryId, avatarEntryId, etc. — derived from *Field when kind='entry', else null.
 *
 * Actions:
 *   setField<K>: generic setter for individual fields
 *   applyEphemeralValue(field, text): sets a parameter field to ephemeral kind
 *   applyEphemeralIngredient(text): replaces ingredientFields with one ephemeral item
 *   applyIdeaSet(idea): sets all 6 parameter fields to ephemeral from a ParsedIdea
 *   reset: clears all fields to initial state
 *
 * serializeBriefForApi: exported pure function that converts store state to
 *   a CreateRunRequest-compatible payload (handles all 3 channels).
 */
import {
  type CreateRunRequest,
  DEFAULT_GENERATION_MODEL_KEY,
  type EntryOrFreeText,
  type GenerationModelKey,
  type ParsedIdea,
  type RunResponse,
} from '@malaberg/shared';
import { create } from 'zustand';

export type BriefFormMode =
  | 'use_existing_structure'
  | 'scratch_premise_set'
  | 'fb_ad_to_advertorial'
  | 'competitor_advertorial_to_advertorial';

// ─── Field value types ────────────────────────────────────────────────────────

export type FieldValue =
  | { kind: 'entry'; entryId: string }
  | { kind: 'value'; value: string }
  | { kind: 'ephemeral'; ephemeralValue: string };

export interface LastSubmittedBriefSnapshot {
  mode: BriefFormMode;
  generationModelKey: GenerationModelKey;
  productId: string | null;
  market: string | null;
  angleField: FieldValue | null;
  avatarField: FieldValue | null;
  problemMechField: FieldValue | null;
  solutionMechField: FieldValue | null;
  awarenessLevelField: FieldValue | null;
  landingPageUrlField?: FieldValue | null;
  ingredientFields: ReadonlyArray<FieldValue>;
  optionalGuidance: string;
}

/** The parameter fields that support the 3-channel model. */
export type ParameterField =
  | 'angle'
  | 'avatar'
  | 'problemMechanism'
  | 'solutionMechanism'
  | 'awarenessLevel';

// ─── Store state ──────────────────────────────────────────────────────────────

export interface BriefFormState {
  mode: BriefFormMode;
  generationModelKey: GenerationModelKey;
  productId: string | null;
  market: string | null;

  // ── 3-channel parameter fields (T6) ────────────────────────────────────────
  angleField: FieldValue | null;
  avatarField: FieldValue | null;
  problemMechField: FieldValue | null;
  solutionMechField: FieldValue | null;
  awarenessLevelField: FieldValue | null;
  landingPageUrlField: FieldValue | null;
  ingredientFields: FieldValue[];

  // ── Backward-compat derived getters (BriefForm.tsx reads these until T8) ──
  /** Derived: entryId when angleField.kind === 'entry', else null */
  angleEntryId: string | null;
  /** Derived: entryId when avatarField.kind === 'entry', else null */
  avatarEntryId: string | null;
  /** Derived: entryId when problemMechField.kind === 'entry', else null */
  problemMechEntryId: string | null;
  /** Derived: entryId when solutionMechField.kind === 'entry', else null */
  solutionMechEntryId: string | null;
  /** Derived: entryId when awarenessLevelField.kind === 'entry', else null */
  awarenessLevelEntryId: string | null;
  /** Derived: entryId when landingPageUrlField.kind === 'entry', else null */
  landingPageUrlEntryId: string | null;
  /** Derived: entryIds from ingredientFields where kind === 'entry' */
  ingredientEntryIds: string[];

  // ── Free-text fields (not reset on product change) ─────────────────────────
  optionalGuidance: string;
  winningStructure: string;
  sourceCopy: string;
  lastSubmittedBrief: LastSubmittedBriefSnapshot | null;

  // ── Actions ────────────────────────────────────────────────────────────────
  setField: <
    K extends keyof Omit<
      BriefFormState,
      | 'setField'
      | 'reset'
      | 'lastSubmittedBrief'
      | 'saveLastSubmittedBrief'
      | 'restoreLastSubmittedBrief'
      | 'applyEphemeralValue'
      | 'applyEphemeralIngredient'
      | 'applyIdeaSet'
      | 'loadFromRun'
    >,
  >(
    key: K,
    value: BriefFormState[K]
  ) => void;

  /**
   * Sets a single parameter field to ephemeral kind.
   * Used when the ideation panel applies an AI suggestion.
   */
  applyEphemeralValue: (field: ParameterField, text: string) => void;

  /**
   * Replaces the ingredients array with a single ephemeral item.
   * AI returns a single hierarchy-preserving string for keyIngredients.
   */
  applyEphemeralIngredient: (text: string) => void;

  /**
   * Applies all 6 fields from a ParsedIdea as ephemeral values.
   * Called from VariableIdeasPanel when user clicks "Use this set".
   */
  applyIdeaSet: (idea: ParsedIdea) => void;

  /**
   * Pre-fills the brief form from a past run (replay path).
   *
   * For each of the 5 single-value fields:
   *   - If a parameterPins row exists for that field type → set as entry (entryId)
   *   - Otherwise → set as ephemeral using the run's denormalized text column
   *
   * For ingredients:
   *   - Walk indices 0..run.ingredients.length-1
   *   - If a pin with displayOrder=i exists → entry kind with that entryId
   *   - Otherwise → ephemeral kind with run.ingredients[i]
   *
   * The update is atomic (single set call) to avoid intermediate-state re-renders.
   */
  loadFromRun: (run: RunResponse) => void;

  saveLastSubmittedBrief: () => void;
  restoreLastSubmittedBrief: () => void;

  reset: (options?: { preserveLastSubmittedBrief?: boolean }) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Derive the entryId if the field is in 'entry' kind, else null. */
function toEntryId(field: FieldValue | null): string | null {
  return field?.kind === 'entry' ? field.entryId : null;
}

/** Derive entryIds from an ingredient fields array (only 'entry' kind). */
function toEntryIds(fields: FieldValue[]): string[] {
  return fields
    .filter((f) => f.kind === 'entry')
    .map((f) => (f as { kind: 'entry'; entryId: string }).entryId);
}

function cloneField(field: FieldValue | null | undefined): FieldValue | null {
  return field ? { ...field } : null;
}

function cloneFields(fields: ReadonlyArray<FieldValue>): FieldValue[] {
  return fields.map((field) => ({ ...field }));
}

function createLastSubmittedBriefSnapshot(state: BriefFormState): LastSubmittedBriefSnapshot {
  return {
    mode: state.mode,
    generationModelKey: state.generationModelKey,
    productId: state.productId,
    market: state.market,
    angleField: cloneField(state.angleField),
    avatarField: cloneField(state.avatarField),
    problemMechField: cloneField(state.problemMechField),
    solutionMechField: cloneField(state.solutionMechField),
    awarenessLevelField: cloneField(state.awarenessLevelField),
    landingPageUrlField: cloneField(state.landingPageUrlField),
    ingredientFields: cloneFields(state.ingredientFields),
    optionalGuidance: state.optionalGuidance,
  };
}

/** Map a ParameterField name to the store key holding its FieldValue. */
function fieldKey(
  field: ParameterField
): keyof Pick<
  BriefFormState,
  'angleField' | 'avatarField' | 'problemMechField' | 'solutionMechField' | 'awarenessLevelField'
> {
  switch (field) {
    case 'angle':
      return 'angleField';
    case 'avatar':
      return 'avatarField';
    case 'problemMechanism':
      return 'problemMechField';
    case 'solutionMechanism':
      return 'solutionMechField';
    case 'awarenessLevel':
      return 'awarenessLevelField';
  }
}

/** Map a ParameterField name to its backward-compat entryId key. */
function entryIdKey(
  field: ParameterField
): keyof Pick<
  BriefFormState,
  | 'angleEntryId'
  | 'avatarEntryId'
  | 'problemMechEntryId'
  | 'solutionMechEntryId'
  | 'awarenessLevelEntryId'
> {
  switch (field) {
    case 'angle':
      return 'angleEntryId';
    case 'avatar':
      return 'avatarEntryId';
    case 'problemMechanism':
      return 'problemMechEntryId';
    case 'solutionMechanism':
      return 'solutionMechEntryId';
    case 'awarenessLevel':
      return 'awarenessLevelEntryId';
  }
}

// ─── Serializer ───────────────────────────────────────────────────────────────

/** The subset of store state that serializeBriefForApi needs. */
export interface SerializableBriefState {
  mode?: BriefFormMode;
  generationModelKey?: GenerationModelKey;
  productId: string | null;
  market: string | null;
  angleField: FieldValue | null;
  avatarField: FieldValue | null;
  problemMechField: FieldValue | null;
  solutionMechField: FieldValue | null;
  awarenessLevelField: FieldValue | null;
  landingPageUrlField?: FieldValue | null;
  ingredientFields: ReadonlyArray<FieldValue>;
  optionalGuidance: string;
  winningStructure: string;
  sourceCopy?: string;
}

/** Convert a single FieldValue to an EntryOrFreeText wire payload. */
function fieldValueToWire(fv: FieldValue): EntryOrFreeText {
  switch (fv.kind) {
    case 'entry':
      return { entryId: fv.entryId };
    case 'value':
      return { value: fv.value };
    case 'ephemeral':
      return { ephemeralValue: fv.ephemeralValue };
  }
}

/**
 * Serialize brief form state to a CreateRunRequest-compatible payload.
 * Exported so use-create-run.ts and tests can consume it.
 *
 * Note: does NOT validate that required fields are non-null — that's the
 * caller's responsibility (use-create-run does its own validation first).
 */
export function serializeBriefForApi(state: SerializableBriefState): CreateRunRequest {
  const mode = state.mode ?? 'use_existing_structure';
  const base = {
    mode,
    generationModelKey: state.generationModelKey ?? DEFAULT_GENERATION_MODEL_KEY,
    productId: state.productId ?? '',
    market: state.market ?? '',
    angle: fieldValueToWire(state.angleField ?? { kind: 'entry', entryId: '' }),
    avatar: fieldValueToWire(state.avatarField ?? { kind: 'entry', entryId: '' }),
    problemMechanism: fieldValueToWire(state.problemMechField ?? { kind: 'entry', entryId: '' }),
    solutionMechanism: fieldValueToWire(state.solutionMechField ?? { kind: 'entry', entryId: '' }),
    awarenessLevel: fieldValueToWire(state.awarenessLevelField ?? { kind: 'entry', entryId: '' }),
    ...(state.landingPageUrlField
      ? { landingPageUrl: fieldValueToWire(state.landingPageUrlField) }
      : {}),
    ingredients: state.ingredientFields.map(fieldValueToWire),
    ...(state.optionalGuidance.trim() ? { optionalGuidance: state.optionalGuidance } : {}),
  };

  if (mode === 'scratch_premise_set') {
    return base as CreateRunRequest;
  }

  if (isAdvertorialMode(mode)) {
    return {
      ...base,
      mode,
      sourceCopy: state.sourceCopy ?? '',
    } as CreateRunRequest;
  }

  return {
    ...base,
    mode: 'use_existing_structure',
    winningStructure: state.winningStructure,
  } as CreateRunRequest;
}

function isAdvertorialMode(
  mode: BriefFormMode
): mode is 'fb_ad_to_advertorial' | 'competitor_advertorial_to_advertorial' {
  return mode === 'fb_ad_to_advertorial' || mode === 'competitor_advertorial_to_advertorial';
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: Omit<
  BriefFormState,
  | 'setField'
  | 'reset'
  | 'saveLastSubmittedBrief'
  | 'restoreLastSubmittedBrief'
  | 'applyEphemeralValue'
  | 'applyEphemeralIngredient'
  | 'applyIdeaSet'
  | 'loadFromRun'
> = {
  mode: 'use_existing_structure',
  generationModelKey: DEFAULT_GENERATION_MODEL_KEY,
  productId: null,
  market: null,
  // 3-channel fields
  angleField: null,
  avatarField: null,
  problemMechField: null,
  solutionMechField: null,
  awarenessLevelField: null,
  landingPageUrlField: null,
  ingredientFields: [],
  // Backward-compat derived fields
  angleEntryId: null,
  avatarEntryId: null,
  problemMechEntryId: null,
  solutionMechEntryId: null,
  awarenessLevelEntryId: null,
  landingPageUrlEntryId: null,
  ingredientEntryIds: [],
  // Free-text
  optionalGuidance: '',
  winningStructure: '',
  sourceCopy: '',
  lastSubmittedBrief: null,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const useBriefFormStore = create<BriefFormState>((set) => ({
  ...initialState,

  setField(key, value) {
    // When setting a *Field key, also update the backward-compat *EntryId.
    set((prev) => {
      const update: Partial<BriefFormState> = { [key]: value };

      // Keep derived fields in sync when *Field changes
      const fvValue = value as FieldValue | null;
      if (key === 'angleField') {
        update.angleEntryId = toEntryId(fvValue);
      } else if (key === 'avatarField') {
        update.avatarEntryId = toEntryId(fvValue);
      } else if (key === 'problemMechField') {
        update.problemMechEntryId = toEntryId(fvValue);
      } else if (key === 'solutionMechField') {
        update.solutionMechEntryId = toEntryId(fvValue);
      } else if (key === 'awarenessLevelField') {
        update.awarenessLevelEntryId = toEntryId(fvValue);
      } else if (key === 'landingPageUrlField') {
        update.landingPageUrlEntryId = toEntryId(fvValue);
      } else if (key === 'ingredientFields') {
        update.ingredientEntryIds = toEntryIds(fvValue as unknown as FieldValue[]);
      }
      // Keep *Field in sync when backward-compat *EntryId setter is called
      else if (key === 'angleEntryId') {
        const id = value as string | null;
        update.angleField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'avatarEntryId') {
        const id = value as string | null;
        update.avatarField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'problemMechEntryId') {
        const id = value as string | null;
        update.problemMechField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'solutionMechEntryId') {
        const id = value as string | null;
        update.solutionMechField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'awarenessLevelEntryId') {
        const id = value as string | null;
        update.awarenessLevelField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'landingPageUrlEntryId') {
        const id = value as string | null;
        update.landingPageUrlField = id ? { kind: 'entry', entryId: id } : null;
      } else if (key === 'ingredientEntryIds') {
        const ids = value as string[];
        update.ingredientFields = ids.map((id) => ({
          kind: 'entry' as const,
          entryId: id,
        }));
      }

      return { ...prev, ...update };
    });
  },

  applyEphemeralValue(field, text) {
    const fk = fieldKey(field);
    const ek = entryIdKey(field);
    const ephemeralField: FieldValue = {
      kind: 'ephemeral',
      ephemeralValue: text,
    };
    set({
      [fk]: ephemeralField,
      [ek]: null, // ephemeral → no entryId
    } as Partial<BriefFormState>);
  },

  applyEphemeralIngredient(text) {
    const ephemeralField: FieldValue = {
      kind: 'ephemeral',
      ephemeralValue: text,
    };
    set({
      ingredientFields: [ephemeralField],
      ingredientEntryIds: [], // no library entries
    });
  },

  applyIdeaSet(idea) {
    set((prev) => {
      const angle: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.angle,
      };
      const avatar: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.avatar,
      };
      const problemMech: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.problemMechanism,
      };
      const solutionMech: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.solutionMechanism,
      };
      const awarenessLevel: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.awarenessLevel,
      };
      const ingredient: FieldValue = {
        kind: 'ephemeral',
        ephemeralValue: idea.keyIngredients,
      };

      return {
        ...prev,
        angleField: angle,
        angleEntryId: null,
        avatarField: avatar,
        avatarEntryId: null,
        problemMechField: problemMech,
        problemMechEntryId: null,
        solutionMechField: solutionMech,
        solutionMechEntryId: null,
        awarenessLevelField: awarenessLevel,
        awarenessLevelEntryId: null,
        ingredientFields: [ingredient],
        ingredientEntryIds: [],
      };
    });
  },

  loadFromRun(run) {
    // Build lookup maps from parameterPins for O(1) access.
    const pins = run.parameterPins ?? [];
    const pinByType = new Map<string, string>();
    const ingredientPinByDisplayOrder = new Map<number, string>();

    for (const p of pins) {
      if (p.parameterType === 'ingredient') {
        ingredientPinByDisplayOrder.set(p.displayOrder, p.parameterEntryId);
      } else {
        pinByType.set(p.parameterType, p.parameterEntryId);
      }
    }

    // Resolve each single-value field: pin → entry, no pin → ephemeral (denorm).
    function resolveSingleField(pinType: string, denormText: string): FieldValue {
      const entryId = pinByType.get(pinType);
      if (entryId !== undefined) {
        return { kind: 'entry', entryId };
      }
      return { kind: 'ephemeral', ephemeralValue: denormText };
    }

    const angleField = resolveSingleField('angle', run.angle);
    const avatarField = resolveSingleField('avatar', run.avatar);
    const problemMechField = resolveSingleField('problem_mechanism', run.problemMechanism);
    const solutionMechField = resolveSingleField('solution_mechanism', run.solutionMechanism);
    const awarenessLevelField = resolveSingleField('awareness_level', run.awarenessLevel);
    const landingPageUrlField = run.landingPageUrl
      ? resolveSingleField('landing_page_url', run.landingPageUrl)
      : null;

    // Resolve ingredient fields: walk indices, pin at displayOrder=i → entry, else ephemeral.
    const ingredientFields: FieldValue[] = (run.ingredients ?? []).map((denormText, i) => {
      const entryId = ingredientPinByDisplayOrder.get(i);
      if (entryId !== undefined) {
        return { kind: 'entry' as const, entryId };
      }
      return { kind: 'ephemeral' as const, ephemeralValue: denormText };
    });

    // Derive backward-compat entryId fields from the resolved FieldValues.
    const ingredientEntryIds = ingredientFields
      .filter((f) => f.kind === 'entry')
      .map((f) => (f as { kind: 'entry'; entryId: string }).entryId);

    const mode: BriefFormMode =
      run.mode === 'fb_ad_to_advertorial' || run.mode === 'competitor_advertorial_to_advertorial'
        ? run.mode
        : !run.mode || run.mode === 'use_existing_structure'
          ? 'use_existing_structure'
          : 'scratch_premise_set';

    // Atomic set — one call to avoid intermediate-state re-renders.
    set({
      mode,
      generationModelKey: run.generationModel?.key ?? DEFAULT_GENERATION_MODEL_KEY,
      productId: run.productId,
      market: run.market,
      angleField,
      angleEntryId: angleField.kind === 'entry' ? angleField.entryId : null,
      avatarField,
      avatarEntryId: avatarField.kind === 'entry' ? avatarField.entryId : null,
      problemMechField,
      problemMechEntryId: problemMechField.kind === 'entry' ? problemMechField.entryId : null,
      solutionMechField,
      solutionMechEntryId: solutionMechField.kind === 'entry' ? solutionMechField.entryId : null,
      awarenessLevelField,
      awarenessLevelEntryId:
        awarenessLevelField.kind === 'entry' ? awarenessLevelField.entryId : null,
      landingPageUrlField,
      landingPageUrlEntryId:
        landingPageUrlField?.kind === 'entry' ? landingPageUrlField.entryId : null,
      ingredientFields,
      ingredientEntryIds,
      optionalGuidance: run.optionalGuidance ?? '',
      winningStructure: mode === 'use_existing_structure' ? run.winningStructure : '',
      sourceCopy: isAdvertorialMode(mode) ? run.winningStructure : '',
    });
  },

  saveLastSubmittedBrief() {
    set((prev) => ({
      lastSubmittedBrief: createLastSubmittedBriefSnapshot(prev),
    }));
  },

  restoreLastSubmittedBrief() {
    set((prev) => {
      const snapshot = prev.lastSubmittedBrief;
      if (!snapshot) return prev;

      const angleField = cloneField(snapshot.angleField);
      const avatarField = cloneField(snapshot.avatarField);
      const problemMechField = cloneField(snapshot.problemMechField);
      const solutionMechField = cloneField(snapshot.solutionMechField);
      const awarenessLevelField = cloneField(snapshot.awarenessLevelField);
      const landingPageUrlField = cloneField(snapshot.landingPageUrlField);
      const ingredientFields = cloneFields(snapshot.ingredientFields);

      return {
        mode: snapshot.mode,
        generationModelKey: snapshot.generationModelKey ?? DEFAULT_GENERATION_MODEL_KEY,
        productId: snapshot.productId,
        market: snapshot.market,
        angleField,
        angleEntryId: toEntryId(angleField),
        avatarField,
        avatarEntryId: toEntryId(avatarField),
        problemMechField,
        problemMechEntryId: toEntryId(problemMechField),
        solutionMechField,
        solutionMechEntryId: toEntryId(solutionMechField),
        awarenessLevelField,
        awarenessLevelEntryId: toEntryId(awarenessLevelField),
        landingPageUrlField,
        landingPageUrlEntryId: toEntryId(landingPageUrlField),
        ingredientFields,
        ingredientEntryIds: toEntryIds(ingredientFields),
        optionalGuidance: snapshot.optionalGuidance,
        winningStructure: '',
        sourceCopy: '',
      };
    });
  },

  reset(options) {
    set((prev) => ({
      ...initialState,
      lastSubmittedBrief: options?.preserveLastSubmittedBrief ? prev.lastSubmittedBrief : null,
    }));
  },
}));
